/**
 * Auto-adds/removes the free-gift line items tied to the rewards progress
 * bar's milestone 1 and milestone 3 (settings.cart_milestone_1/3_gift_
 * product) as spend crosses their thresholds — the discount that makes them
 * ring up free (see Shopify admin) only discounts a line that's already in
 * the cart, it never adds one, so this fills that gap.
 *
 * Reconciliation runs on load and on every PUB_SUB_EVENTS.cartUpdate: fetch
 * the live cart, compare each gift's actual line quantity against what
 * spend (excluding the gifts themselves — see cart-shipping-bar.liquid)
 * says it should be, and if anything's off, set both in one /cart/update.js
 * call. That call's own resulting cartUpdate re-enters this function, but
 * since the second pass finds nothing left to change it stops there instead
 * of looping.
 *
 * On the /cart page, the update call also asks for the main-cart-footer
 * section back (mirroring cart.js's own CartItems#updateQuantity) and swaps
 * its .js-contents in directly, since main-cart-footer's totals/savings-
 * badge content is entirely block-driven (section.blocks) and
 * refreshDawnCartUI's generic refresh doesn't reach it. The section id
 * requested MUST be read from main-cart-footer's own dataset.id (its real,
 * template-scoped id, e.g. "template--xyz__cart-footer") rather than the
 * literal string "main-cart-footer" — requesting the bare file name renders
 * the section without its configured blocks (verified directly: same empty
 * result via /cart/change.js, the endpoint the working quantity stepper
 * itself uses, once the literal name is substituted in instead of the real
 * dataset.id).
 */
(function () {
  function cartUrl(path) {
    var base = (typeof routes !== 'undefined' && routes.cart_url) || '/cart';
    return base + path;
  }

  function swapCartFooter(sectionsHtml, sectionId) {
    var footer = document.getElementById('main-cart-footer');
    var mount = footer && footer.querySelector('.js-contents');
    var html = sectionsHtml && sectionsHtml[sectionId];
    if (!mount || !html) return;
    var source = new DOMParser().parseFromString(html, 'text/html').querySelector('#main-cart-footer .js-contents');
    if (source) mount.replaceChildren.apply(mount, source.childNodes);
  }

  function getConfig() {
    var el = document.getElementById('CartGiftsConfig');
    if (!el) return null;
    try {
      var config = JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
    if (!config.gifts || !config.gifts.length) return null;
    return config;
  }

  var reconciling = false;
  var pending = false;

  function reconcile() {
    if (reconciling) {
      pending = true;
      return;
    }
    var config = getConfig();
    if (!config) return;

    reconciling = true;
    var footerSectionId = document.getElementById('main-cart-footer') && document.getElementById('main-cart-footer').dataset.id;

    fetch(cartUrl('.js'), { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var giftVariantIds = config.gifts.map(function (g) {
          return g.variantId;
        });
        var giftLinesByVariant = {};
        var spend = 0;
        cart.items.forEach(function (item) {
          if (giftVariantIds.indexOf(item.variant_id) !== -1) {
            (giftLinesByVariant[item.variant_id] = giftLinesByVariant[item.variant_id] || []).push(item);
          } else {
            spend += item.original_line_price;
          }
        });

        var updates = {};
        var changed = false;
        config.gifts.forEach(function (gift) {
          var want = spend >= gift.threshold ? 1 : 0;
          var lines = giftLinesByVariant[gift.variantId] || [];
          var have = lines.reduce(function (sum, line) {
            return sum + line.quantity;
          }, 0);

          // Same variant can legitimately span more than one cart line (distinct
          // line properties). Zero every existing line by its unique key — a
          // variant-id-keyed update only ever touches the first matching line —
          // then re-add a single clean line at the wanted quantity.
          if (have === want && lines.length <= 1) return;

          lines.forEach(function (line) {
            updates[line.key] = 0;
          });
          if (want > 0) {
            updates[gift.variantId] = want;
          }
          changed = true;
        });

        if (!changed) return null;

        var body = { updates: updates };
        if (footerSectionId) {
          body.sections = [footerSectionId];
          body.sections_url = window.location.pathname;
        }

        return fetch(cartUrl('/update.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        }).then(function (r) {
          return r.json();
        });
      })
      .then(function (updateResponse) {
        if (!updateResponse) return;
        if (footerSectionId) swapCartFooter(updateResponse.sections, footerSectionId);
        if (typeof window.refreshDawnCartUI === 'function') {
          return window.refreshDawnCartUI();
        }
      })
      .catch(function (e) {
        if (window.console && console.error) console.error('cart-gifts:', e);
      })
      .finally(function () {
        reconciling = false;
        if (pending) {
          pending = false;
          reconcile();
        }
      });
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, reconcile);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }

  // Gift-line quantities are only ever correct once a reconcile pass has
  // fully landed. The checkout button is a plain form submit straight to
  // Shopify — nothing normally makes it wait, so a click mid-reconcile (or
  // right after a mutation that hasn't been reconciled yet) can send the
  // customer to checkout with stale/duplicate gift quantities. Hold the
  // submit until reconciliation is idle, then resubmit programmatically.
  //
  // Forces a fresh reconcile() on the first click rather than only waiting
  // on one that happens to already be running — reconciling/pending both
  // being false at click time doesn't prove the live cart was ever actually
  // checked (e.g. this is the first reconcile this page would trigger, or
  // the cart changed through some path this script didn't get a cartUpdate
  // for). Only a pass started now, and waited out, can guarantee that.
  //
  // checkoutVerified is what stops that from looping: form.requestSubmit()
  // re-fires this same 'submit' listener with the same submitter (unlike
  // form.submit(), which doesn't re-fire listeners at all). Without a flag
  // marking "already forced, don't do it again", that re-fire would call
  // reconcile()/preventDefault() a second time, wait, resubmit again, get
  // caught again — forever, so the checkout button would never actually
  // reach Shopify. The flag makes the guard one-shot: intercept once, force
  // the check, then let the programmatic resubmit straight through.
  var checkoutVerified = false;
  document.addEventListener(
    'submit',
    function (event) {
      // `name="checkout"` lives on the <button> that triggered the submit
      // (id="CartDrawer-Checkout" / main-cart-footer's checkout button), not
      // on the <form> itself (event.target) — has to be read off
      // event.submitter, the actual clicked button, not the form.
      var submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout') return;
      if (checkoutVerified) {
        checkoutVerified = false;
        return;
      }

      var form = event.target;
      event.preventDefault();
      reconcile();
      (function waitForSettle() {
        if (!reconciling && !pending) {
          checkoutVerified = true;
          form.requestSubmit(submitter);
        } else {
          setTimeout(waitForSettle, 50);
        }
      })();
    },
    true
  );
})();
